import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { Accordion, AccordionDetails, AccordionSummary, Typography } from '@mui/material';
import { ArrowCheckIcon } from '@sharedCR/coreDesignSystem/icons';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { IconDirection } from 'constants/coreDesignSystem';
import { PAGE_CODES } from 'constants/pageCodes';
import { getPendingChangesInfo } from 'utils/loanFees';

import { getMasterPermissionsToPage } from 'store/auth/selectors';
import { selectEditableFeesSections } from 'store/loanApplications/selectors';

import { getAccordionStyles } from '../LoanFeesBody.styles';

import AccordionDetailsEdit from './components/AccordionDetailsEdit';
import AccordionDetailsView from './components/AccordionDetailsView';
import FeesMoreIcon from './components/FeesMoreIcon';
import PendingChangesNumber from './components/Label/PendingChangesNumber';
import TotalAmount from './components/Label/TotalAmount';

const LoanFeeSection = ({
  name = '',
  description = '',
  cdSection,
  isNameAmountSection = false,
  isWithHeader = true,
  isNonBorrowerPaidFieldsIncluded = true,
  isFeeProviderColumnVisible,
  labelNameAmountSection = 'Name',
  items,
  groups,
  isTotalView = false,
  alwaysExpanded = false,
  totalAmount: initialTotalAmount,
  defaultExpanded = false,
  amountColumnName = 'Amount',
  leSection,
  editedSection,
  setEditedSection,
  setIsLoading,
  isLoading,
}) => {
  const ref = useRef();

  const editableSections = useSelector(selectEditableFeesSections);
  const { isEditAllowed } = useSelector(getMasterPermissionsToPage(PAGE_CODES.LOAN_FEES));

  const isOpened = editedSection?.name === cdSection;
  const isEditableSection = editableSections.includes(cdSection);

  const isNotEmptyCellsSection =
    isTotalView || !isNonBorrowerPaidFieldsIncluded || isNameAmountSection;
  const accordionHeaderId = `panel${cdSection}-header`;

  const [isExpanded, setIsExpanded] = useState(alwaysExpanded || defaultExpanded);
  const [{ pendingChangesNumber, isTotalAmountPending }, setPendingChangesInfo] = useState({});

  useEffect(() => {
    const pendingChangesInfo = getPendingChangesInfo(ref);
    setPendingChangesInfo(pendingChangesInfo);
  }, [groups, items, isOpened]);

  const onOpenSection = useCallback(
    e => {
      const { section, isToAdd } = e.currentTarget.dataset;
      setEditedSection({ name: section, isToAdd: isToAdd === 'true' });
    },
    [setEditedSection],
  );

  const onToggleAccordion = useCallback(
    value => {
      if (alwaysExpanded) {
        return;
      }

      setIsExpanded(prev => {
        if (_.isBoolean(value)) {
          return value;
        }

        const { target } = value;

        const isInitiatedByAccordionClick =
          target.id === accordionHeaderId || !!target.closest(`#${accordionHeaderId}`);

        return isInitiatedByAccordionClick ? !prev : prev;
      });
    },
    [alwaysExpanded, accordionHeaderId],
  );

  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      expanded={isExpanded}
      ref={ref}
      sx={getAccordionStyles({ alwaysExpanded, isOpened })}
      onChange={onToggleAccordion}
    >
      {!alwaysExpanded && (
        <AccordionSummary
          aria-controls={`panel${cdSection}-content`}
          expandIcon={!isOpened && <ArrowCheckIcon direction={IconDirection.DOWN} />}
          id={accordionHeaderId}
        >
          <div className="flex items-center flex-1 justify-between min-w-0">
            <span className="min-w-0">
              <span className="inline-flex flex-col pr-8 w-400 max-w-full">
                <Typography variant="bold16">{`${cdSection}. ${name}`}</Typography>
                {!!description && !isLoading && (
                  <Typography variant="regular12">{description}</Typography>
                )}
              </span>

              <span id={`total-loan-fee-section-${cdSection}`}>
                {!isOpened && (
                  <TotalAmount isPending={isTotalAmountPending} value={initialTotalAmount} />
                )}
              </span>

              <span id={`pending-changes-number-${cdSection}`}>
                {!isOpened && <PendingChangesNumber value={pendingChangesNumber} />}
              </span>
            </span>

            {!isOpened && isEditAllowed && isEditableSection && (
              <FeesMoreIcon
                cdSection={cdSection}
                editedSection={editedSection}
                isLoading={isLoading}
                onOpenSection={onOpenSection}
                onToggleAccordion={onToggleAccordion}
              />
            )}
          </div>
        </AccordionSummary>
      )}

      <AccordionDetails>
        {isOpened && (
          <AccordionDetailsEdit
            cdSection={cdSection}
            groups={groups}
            isLoading={isLoading}
            isNonBorrowerPaidFieldsIncluded={isNonBorrowerPaidFieldsIncluded}
            isWithHeader={isWithHeader}
            items={items}
            labelNameAmountSection={labelNameAmountSection}
            leSection={leSection}
            parentRef={ref}
            setEditedSection={setEditedSection}
            setIsLoading={setIsLoading}
          />
        )}

        {!isOpened && (
          <AccordionDetailsView
            amountColumnName={amountColumnName}
            cdSection={cdSection}
            groups={groups}
            isFeeProviderColumnVisible={isFeeProviderColumnVisible}
            isLoading={isLoading}
            isNameAmountSection={isNameAmountSection}
            isNonBorrowerPaidFieldsIncluded={isNonBorrowerPaidFieldsIncluded}
            isNotEmptyCellsSection={isNotEmptyCellsSection}
            isTotalView={isTotalView}
            isWithHeader={isWithHeader}
            items={items}
            labelNameAmountSection={labelNameAmountSection}
          />
        )}
      </AccordionDetails>
    </Accordion>
  );
};

LoanFeeSection.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  setEditedSection: PropTypes.func.isRequired,
  setIsLoading: PropTypes.func.isRequired,
  labelNameAmountSection: PropTypes.string,
  amountColumnName: PropTypes.string,
  leSection: PropTypes.string,
  cdSection: PropTypes.string,
  defaultExpanded: PropTypes.bool,
  isTotalView: PropTypes.bool,
  editedSection: PropTypes.object,
  groups: PropTypes.arrayOf(
    PropTypes.shape({
      items: PropTypes.array,
      isFeeProviderColumnVisible: PropTypes.bool,
    }),
  ),
  alwaysExpanded: PropTypes.bool,
  isNameAmountSection: PropTypes.bool,
  isNonBorrowerPaidFieldsIncluded: PropTypes.bool,
  isFeeProviderColumnVisible: PropTypes.bool,
  isWithHeader: PropTypes.bool,
  items: PropTypes.array,
  name: PropTypes.string,
  description: PropTypes.string,
  totalAmount: PropTypes.number,
};

export default LoanFeeSection;
